/* GHALIA — Cart drawer Quick Add (size / variant picker) */

(() => {
  const SKIP_OPTION_NAMES = [
    'material',
    'metal',
    'finish',
    'colour',
    'color',
    'الخامة',
    'لون',
    'اللون',
    'لونان',
  ];
  const SIZE_OPTION_HINTS = ['size', 'ring size', 'مقاس', 'مقاس الخاتم'];

  let deps = {};
  let state = {
    product: null,
    selected: [],
    variant: null,
    trigger: null,
    openCart: true,
    bundleMembers: {},
    bundleMembersLoading: false,
    memberSizes: {},
  };

  const memberProductCache = new Map();

  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  const strings = () => window.Shopify?.strings?.quickAdd || {};

  const money = (cents) => {
    if (window.GhaliaPricing?.formatNet) return window.GhaliaPricing.formatNet(cents);
    if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents);
    return (cents / 100).toFixed(2);
  };

  const escapeHtml = (value) =>
    String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

  const NON_SIZE_VALUES = ['default title', 'standard', 'one size', 'os', 'n/a'];

  const isNativeBundleProduct = (product) => {
    const handle = String(product?.handle || '').trim();
    if (handle && window.GhaliaNativeBundleHandles?.has(handle)) return true;
    const tags = (product?.tags || []).map((t) => String(t).toLowerCase());
    return tags.some((t) => t.includes('ghalia-bundle') || t === 'bundle');
  };

  const shouldSkipOption = (name, values) => {
    const n = String(name || '')
      .toLowerCase()
      .trim();
    if (SKIP_OPTION_NAMES.some((s) => n.includes(s))) return true;
    if (n === 'title' && values?.length <= 1) return true;
    const vals = values || [];
    if (
      vals.length <= 1 &&
      vals.every((v) => NON_SIZE_VALUES.includes(String(v || '').toLowerCase().trim()))
    ) {
      return true;
    }
    if ((n.includes('earring') || n === 'earring') && !looksLikeSizeValues(vals)) {
      return true;
    }
    if (
      (n.includes('necklace') || n.includes('choker')) &&
      !looksLikeSizeValues(vals) &&
      vals.length <= 1
    ) {
      return true;
    }
    return false;
  };

  const normalizeOptionName = (raw, index) => {
    if (typeof raw === 'string') return raw.trim();
    if (raw && typeof raw === 'object') {
      return String(raw.name || raw.label || raw.title || '').trim();
    }
    return '';
  };

  const optionNameAt = (product, index) => {
    const raw = product?.options?.[index];
    const name = normalizeOptionName(raw, index);
    if (name) return name;
    const s = strings();
    return s.ringSize || s.sizeLabel || 'Size';
  };

  const looksLikeSizeValues = (values) =>
    (values || []).some((v) => /\(eu\)|\(us\)|\(uk\)/i.test(String(v)));

  const isSizeOption = (name, values) => {
    const n = String(name || '').toLowerCase();
    if (SIZE_OPTION_HINTS.some((hint) => n.includes(hint))) return true;
    return looksLikeSizeValues(values);
  };

  const getOptionValues = (product, optionIndex) => {
    const values = new Set();
    product.variants.forEach((variant) => {
      const val = [variant.option1, variant.option2, variant.option3][optionIndex];
      if (val) values.add(val);
    });
    return [...values];
  };

  const variantOptions = (variant) => {
    if (Array.isArray(variant.options) && variant.options.some((o) => o != null && o !== '')) {
      return variant.options;
    }
    return [variant.option1, variant.option2, variant.option3].filter((o) => o != null && o !== '');
  };

  const getSelectableOptions = (product) => {
    const names = product.options || [];
    const count = Math.max(
      names.length,
      ...product.variants.map((v) => variantOptions(v).length),
      0
    );
    return Array.from({ length: count }, (_, index) => {
      const values = getOptionValues(product, index);
      const name = optionNameAt(product, index);
      return { name, index, values };
    }).filter((opt) => !shouldSkipOption(opt.name, opt.values) && opt.values.length > 1);
  };

  const needsQuickAdd = (product) => {
    const selectable = getSelectableOptions(product);
    if (selectable.length) {
      const segment = productSizeSegment(product);
      if (segment === 'earrings' || segment === 'necklaces') {
        return selectable.some((opt) => looksLikeSizeValues(opt.values));
      }
      return true;
    }
    const variants = product?.variants || [];
    if (variants.length <= 1) return false;
    const signatures = new Set(
      variants.map((v) =>
        [v.option1, v.option2, v.option3].filter((o) => o != null && o !== '').join('|')
      )
    );
    if (signatures.size <= 1) return false;
    return variants.some((v) =>
      [v.option1, v.option2, v.option3].some((o) => {
        const text = String(o || '');
        return looksLikeSizeValues([text]) || /size|مقاس/i.test(text);
      })
    );
  };

  const ensureDeps = () => {
    if (deps.addVariantToCart && deps.cartDrawer) return deps;
    if (window.GhaliaCartQuickAddDeps) {
      deps = { ...window.GhaliaCartQuickAddDeps };
    }
    return deps;
  };

  const openQuickAddPicker = async (product, btn, openCart) => {
    ensureDeps();
    await openPanel(product, { trigger: btn, openCart });
    const drawer = deps.cartDrawer || document.querySelector('[data-cart-drawer]');
    if (!drawer) return;
    if (deps.openDrawer) {
      deps.openDrawer(drawer, { preserveQuickAdd: true });
    } else {
      drawer.dataset.open = 'true';
      drawer.setAttribute('aria-hidden', 'false');
      drawer.classList.add('is-quick-add-open');
      document.documentElement.classList.add('drawer-open');
    }
  };

  const productMaterialLine = (product) => {
    const s = strings();
    const title = String(product?.title || '').toLowerCase();
    if (title.includes('sterling') && title.includes('gold')) return s.materialTwoTone || '';
    if (title.includes('sterling') || title.includes('silver') || title.includes('فض')) {
      return s.materialSterling || '';
    }
    if (title.includes('gold') || title.includes('ذهب')) return s.materialGold || '';
    return '';
  };

  const RING_SIZE_EU = {
    4: { eu: '47', label: 'Extra small' },
    4.5: { eu: '48', label: 'Extra small' },
    5: { eu: '50', label: 'XSmall' },
    5.5: { eu: '51', label: 'Small' },
    6: { eu: '52', label: 'Medium' },
    6.25: { eu: '53', label: 'Medium' },
    6.5: { eu: '53', label: 'Medium' },
    7: { eu: '54', label: 'Large' },
    7.5: { eu: '55', label: 'Large' },
    8: { eu: '57', label: 'Extra large' },
    8.5: { eu: '58', label: 'Extra large' },
    9: { eu: '59', label: 'XXL' },
    10: { eu: '61', label: 'XXL' },
  };

  const normalizeSizeLabel = (labelRaw) => {
    if (!labelRaw) return '';
    const raw = labelRaw.trim();
    const compact = raw.toLowerCase().replace(/\s+/g, '');
    if (/^(xs[-–]?small|xsmall|x-small|xs)$/.test(compact)) return 'XSmall';
    if (raw.includes('-')) {
      const first = raw.split('-')[0].trim().toLowerCase();
      if (first === 'xs') return 'XSmall';
      return raw.split('-').pop().trim();
    }
    return raw;
  };

  const lookupRingSizeMeta = (pillRaw) => {
    const us = String(pillRaw || '').trim();
    const num = parseFloat(us);
    if (Number.isNaN(num)) return { eu: '', label: '', us };
    const hit = RING_SIZE_EU[num] || RING_SIZE_EU[Math.round(num)];
    return hit ? { eu: hit.eu, label: hit.label, us } : { eu: '', label: '', us };
  };

  const parseRingSizeValue = (raw) => {
    if (!raw || typeof raw !== 'string') return { pill: '', eu: '', label: '', us: '' };
    const segments = raw.split('(').filter((p) => p.length);
    if (segments.length < 2) {
      const us = raw.trim();
      const meta = lookupRingSizeMeta(us);
      return { pill: us, eu: meta.eu, label: meta.label, us: meta.us || us };
    }
    const pill = (segments[0].split(')')[1] || segments[0] || '').trim().split('/')[0].trim();
    const labelRaw = segments[segments.length - 1].replace(/\)\s*$/, '').trim();
    const euPart = segments[segments.length - 2] || '';
    let eu = (euPart.split(')')[1] || '').trim().match(/\d{2,3}/)?.[0] || '';
    let label = normalizeSizeLabel(labelRaw);
    if (pill === '5' && eu === '50' && /^small$/i.test(label)) label = 'XSmall';
    if (!eu || !label) {
      const meta = lookupRingSizeMeta(pill);
      eu = eu || meta.eu;
      label = label || meta.label;
    }
    const usMatch = raw.match(/\(US\)\s*([\d.]+)/i);
    const us = usMatch ? usMatch[1] : pill;
    return { pill, eu, label, us };
  };

  const formatSizeLabelDisplay = (labelRaw) => {
    if (!labelRaw) return '';
    return String(labelRaw).trim().replace(/\s+/g, '').toUpperCase();
  };

  const formatUsSizeDetail = (template, us, labelRaw) => {
    const label = formatSizeLabelDisplay(labelRaw);
    let out = template
      .replace(/\{\{\s*us_size\s*\}\}/g, us)
      .replace(/\{\{\s*size_label\s*\}\}/g, label);
    if (!label) out = out.replace(/\s*·\s*$/g, '').replace(/\s*·\s*·/g, ' · ');
    return out.trim();
  };

  const pillDisplay = (value) => {
    const parsed = parseRingSizeValue(String(value || ''));
    if (parsed.eu) return parsed.eu;
    const raw = String(value || '').trim();
    const eu = raw.match(/\(EU\)\s*(\d+)/i) || raw.match(/EU\s*(\d+)/i);
    if (eu) return eu[1];
    const uk = raw.match(/\(UK\)\s*([A-Za-z]+)/i);
    if (uk) return uk[1].toUpperCase();
    const us = raw.match(/\(US\)\s*([\d.]+)/i);
    if (us) return us[1];
    const short = raw.split('(')[0].split('/').pop().trim();
    return short.length <= 4 ? short : raw.split('(')[0].split('/')[0].trim();
  };

  const sizeOptionLabel = (name, values, product) => {
    if (product) return sizeLabelForOption(product, name, values);
    const s = strings();
    if (isSizeOption(name, values)) return s.ringSize || s.sizeLabel || name || 'Size';
    return name || 'Option';
  };

  const normalizeSize = (value) => String(value || '').replace(/\s+/g, ' ').trim();

  const euFromOptionString = (value) => {
    const s = String(value || '');
    const euTag = s.match(/\(EU\)\s*(\d{2})/i);
    if (euTag) return euTag[1];
    const nums = s.match(/\b(4[7-9]|5[0-9]|6[01])\b/g);
    if (nums?.length) return nums[nums.length - 1];
    return normalizeSize(s);
  };

  const sizeValuesMatch = (a, b) => {
    if (!a || !b) return false;
    if (normalizeSize(a) === normalizeSize(b)) return true;
    const euA = euFromOptionString(a);
    const euB = euFromOptionString(b);
    return Boolean(euA && euB && euA === euB);
  };

  const isNativeMultiSizeBundle = (product) =>
    isNativeBundleProduct(product) && getSelectableOptions(product).length >= 3;

  const getMemberSizeOptionIndex = (memberProduct) => {
    if (!memberProduct?.variants?.length) return 0;
    const count = Math.max((memberProduct.options || []).length, 3);
    for (let index = 0; index < count; index += 1) {
      const name = optionNameAt(memberProduct, index).toLowerCase();
      if (name === 'title' || name.includes('color') || name.includes('colour') || name.includes('material')) {
        continue;
      }
      if (name.includes('size') || name.includes('ring') || name.includes('bracelet') || name.includes('مقاس')) {
        return index;
      }
    }
    return 0;
  };

  const getMemberSizeValues = (memberProduct) => {
    if (!memberProduct?.variants?.length) return [];
    const index = getMemberSizeOptionIndex(memberProduct);
    const values = [];
    const seen = new Set();
    memberProduct.variants.forEach((variant) => {
      const val = [variant.option1, variant.option2, variant.option3][index];
      if (!val || seen.has(val)) return;
      seen.add(val);
      values.push(val);
    });
    return values;
  };

  const memberSizeAvailable = (memberProduct, value) => {
    if (!memberProduct?.variants?.length || !value) return false;
    const index = getMemberSizeOptionIndex(memberProduct);
    return memberProduct.variants.some((variant) => {
      if (!variant.available) return false;
      const val = [variant.option1, variant.option2, variant.option3][index];
      return sizeValuesMatch(val, value);
    });
  };

  const initMemberSizes = (product, bundleMembers) => {
    const selectable = getSelectableOptions(product);
    const optionByMember = assignBundleOptionsToMembers(product, selectable, bundleMembers);
    const memberSizes = {};
    optionByMember.forEach((opt, handle) => {
      const member = bundleMembers[handle];
      const values = member ? getMemberSizeValues(member) : opt.values;
      const sorted = sortOptionValues(product, opt.name, values);
      const current = state.selected[opt.index];
      memberSizes[handle] =
        (current && sorted.find((v) => sizeValuesMatch(v, current))) ||
        sorted[0] ||
        '';
    });
    return memberSizes;
  };

  const syncSelectedFromMemberSizes = (product, bundleMembers) => {
    const selectable = getSelectableOptions(product);
    const optionByMember = assignBundleOptionsToMembers(product, selectable, bundleMembers);
    optionByMember.forEach((opt, handle) => {
      const pick = state.memberSizes[handle];
      if (pick) state.selected[opt.index] = pick;
    });
  };

  const findMultiBundleVariant = (product, memberSizes, bundleMembers) => {
    const handles = getBundleComponentHandles(product.handle);
    const selectable = getSelectableOptions(product);
    const optionByMember = assignBundleOptionsToMembers(product, selectable, bundleMembers);

    const matchesSelection = (variant) => {
      if (!variant.available) return false;
      const opts = variantOptions(variant);
      return handles.every((handle) => {
        if (!memberNeedsSizePicker(handle, bundleMembers?.[handle])) return true;
        const opt = optionByMember.get(handle);
        if (!opt) return true;
        const pick = memberSizes[handle];
        if (!pick) return false;
        const pickEu = euFromOptionString(pick);
        const varEu = euFromOptionString(opts[opt.index]);
        if (pickEu && varEu) return pickEu === varEu;
        return sizeValuesMatch(opts[opt.index], pick);
      });
    };

    return product.variants.find(matchesSelection) || null;
  };

  const findVariant = (product, selected) => {
    const count = Math.max((product.options || []).length, 3);
    return product.variants.find((variant) => {
      if (!variant.available) return false;
      const opts = variantOptions(variant);
      return Array.from({ length: count }, (_, idx) => idx).every((idx) => {
        const pick = selected[idx];
        if (pick === undefined || pick === null || pick === '') return true;
        return sizeValuesMatch(opts[idx], pick);
      });
    });
  };

  const isValueAvailable = (product, optionIndex, value, selected) =>
    product.variants.some((variant) => {
      if (!variant.available) return false;
      const opts = variantOptions(variant);
      if (!sizeValuesMatch(opts[optionIndex], value)) return false;
      return (product.options || []).every((_, idx) => {
        if (idx === optionIndex) return true;
        const pick = selected[idx];
        if (pick === undefined || pick === null || pick === '') return true;
        return sizeValuesMatch(opts[idx], pick);
      });
    });

  const productSizeSegment = (product) => {
    const type = String(product?.product_type || '').toLowerCase();
    const title = String(product?.title || '').toLowerCase();
    const handle = String(product?.handle || '').toLowerCase();
    const tags = (product?.tags || []).map((t) => String(t).toLowerCase());
    if (isNativeBundleProduct(product)) return 'bundle';
    if (
      handle.includes('earring') ||
      handle.includes('hoop') ||
      (type.includes('earring') && !title.includes('ring')) ||
      (title.includes('earring') && !title.includes('ring') && !title.includes('+')) ||
      tags.some((t) => t.includes('earring') && !t.includes('ring'))
    ) {
      return 'earrings';
    }
    if (
      type.includes('necklace') ||
      title.includes('necklace') ||
      title.includes('choker') ||
      tags.some((t) => t.includes('necklace') || t.includes('choker'))
    ) {
      return 'necklaces';
    }
    if (type.includes('bracelet') || title.includes('bracelet') || tags.some((t) => t.includes('bracelet'))) {
      return 'bracelets';
    }
    if (type.includes('ring') || title.includes(' ring') || tags.some((t) => t.includes('ring'))) {
      return 'rings';
    }
    return 'rings';
  };

  const optionSizeSegment = (product, optionName, values) => {
    const vals = values || [];
    const n = String(optionName || '').toLowerCase();
    if (looksLikeSizeValues(vals)) {
      if (n.includes('bracelet') || n.includes('wrist') || n.includes('سوار')) return 'bracelets';
      if (n.includes('ring') || n.includes('pave') || n.includes('bridge') || n.includes('knot')) {
        return 'rings';
      }
      if (n.includes('earring') || n.includes('قرط')) {
        return looksLikeSizeValues(vals) ? 'rings' : 'earrings';
      }
      return 'rings';
    }
    if (n.includes('ring') && n.includes('size')) return 'rings';
    if (n.includes('bracelet') || n.includes('wrist')) return 'bracelets';
    if (n.includes('necklace') || n.includes('choker')) return 'necklaces';
    if (n.includes('earring')) return 'earrings';
    return productSizeSegment(product);
  };

  const usesCompactRingPills = (product, optionName, values) =>
    optionSizeSegment(product, optionName, values) === 'rings' || looksLikeSizeValues(values);

  const sizeLabelForOption = (product, optionName, values) => {
    const s = strings();
    if (!isSizeOption(optionName, values)) return optionName || 'Option';
    const segment = optionSizeSegment(product, optionName, values);
    const n = String(optionName || '').toLowerCase();
    if (segment === 'rings') {
      if (n.includes('bridge') && n.includes('bracelet')) {
        return s.bridgeBraceletSize || s.braceletSize || 'Bridge bracelet size';
      }
      if (n.includes('knot') && n.includes('bracelet')) {
        return s.knotBraceletSize || s.braceletSize || 'KNOT bracelet size';
      }
      if (n.includes('pave') && n.includes('ring')) return s.paveRingSize || s.ringSize || 'Pave ring size';
      if (n.includes('bridge') && n.includes('ring')) {
        return s.bridgeRingSize || s.ringSize || 'Bridge ring size';
      }
      if (n.includes('earring') && looksLikeSizeValues(values)) {
        return s.ringSize || s.sizeLabel || 'Ring size';
      }
      return s.ringSize || s.sizeLabel || 'Ring size';
    }
    if (segment === 'bracelets') {
      if (n.includes('bridge') && n.includes('bracelet')) {
        return s.bridgeBraceletSize || s.braceletSize || 'Bridge bracelet size';
      }
      if (n.includes('knot') && n.includes('bracelet')) {
        return s.knotBraceletSize || s.braceletSize || 'KNOT bracelet size';
      }
      return s.braceletSize || s.sizeLabel || 'Bracelet size';
    }
    if (segment === 'necklaces') return s.necklaceSize || s.sizeLabel || 'Necklace size';
    if (segment === 'earrings') return s.earringSize || s.sizeLabel || 'Earring size';
    return s.ringSize || s.sizeLabel || 'Ring size';
  };

  const pillDisplayFor = (product, optionName, values, value) => {
    if (usesCompactRingPills(product, optionName, values)) return pillDisplay(value);
    return String(value || '').trim();
  };

  const APPAREL_SIZE_ORDER = ['XS-S', 'M-L', 'XL-XXL'];

  const apparelSizeRank = (value) => {
    const norm = String(value || '')
      .trim()
      .toUpperCase()
      .replace(/\s+/g, '');
    const idx = APPAREL_SIZE_ORDER.findIndex((key) => key.toUpperCase() === norm);
    if (idx >= 0) return idx;
    if (/^XS|X-?S|XSMALL|EXTRA.?SMALL/i.test(norm)) return 0;
    if (/^M-?L|MEDIUM|LARGE(?!\s*XL)/i.test(norm) && !/XL|XXL/i.test(norm)) return 1;
    if (/XL|XXL|2XL|EXTRA.?LARGE/i.test(norm)) return 2;
    return 999;
  };

  const ringSizeSortKey = (value) => {
    const parsed = parseRingSizeValue(String(value || ''));
    if (parsed.eu) {
      const eu = parseInt(parsed.eu, 10);
      if (!Number.isNaN(eu)) return eu;
    }
    const us = parseFloat(parsed.us || parsed.pill);
    if (!Number.isNaN(us)) return us * 10;
    return 999;
  };

  const sortOptionValues = (product, optionName, values) => {
    const list = [...(values || [])];
    if (list.length < 2) return list;
    if (looksLikeSizeValues(values) || usesCompactRingPills(product, optionName, values)) {
      return list.sort((a, b) => ringSizeSortKey(a) - ringSizeSortKey(b));
    }
    const segment = optionSizeSegment(product, optionName, values);
    if (segment === 'bracelets' || segment === 'necklaces' || segment === 'earrings') {
      return list.sort((a, b) => apparelSizeRank(a) - apparelSizeRank(b));
    }
    return list;
  };

  const sizeGuideSegmentFor = (product, selectable) => {
    const segments = (selectable || [])
      .filter((opt) => isSizeOption(opt.name, opt.values))
      .map((opt) => optionSizeSegment(product, opt.name, opt.values));
    if (segments.includes('bracelets')) return 'bracelets';
    if (segments.includes('rings')) return 'rings';
    return productSizeSegment(product) === 'bundle' ? 'rings' : productSizeSegment(product);
  };

  const getBundleComponentHandles = (bundleHandle) =>
    window.GhaliaNativeBundleComponents?.[bundleHandle] || [];

  const memberNeedsSizePicker = (handle, memberProduct) => {
    if (!handle) return false;
    const h = String(handle).toLowerCase();
    if (h.includes('earring') || h.includes('hoop') || h.includes('necklace') || h.includes('choker')) {
      return false;
    }
    if (h.includes('bracelet') || h.includes('ring')) return true;
    if (memberProduct) {
      const type = String(memberProduct.product_type || '').toLowerCase();
      const title = String(memberProduct.title || '').toLowerCase();
      if (
        type.includes('earring') ||
        title.includes('earring') ||
        type.includes('necklace') ||
        title.includes('necklace') ||
        title.includes('choker')
      ) {
        return false;
      }
      if (type.includes('bracelet') || title.includes('bracelet')) return true;
      if (type.includes('ring') || title.includes(' ring')) return true;
    }
    return false;
  };

  const ringComponentHandles = (components) =>
    (components || []).filter((h) => {
      const hl = String(h).toLowerCase();
      return hl.includes('ring') && !hl.includes('earring');
    });

  const resolveOptionMemberHandle = (bundleHandle, opt, bundleMembers) => {
    const components = getBundleComponentHandles(bundleHandle);
    const name = String(opt.name || '').toLowerCase();

    if (name.includes('bracelet')) {
      const bracelets = components.filter((h) => h.toLowerCase().includes('bracelet'));
      if (bracelets.length === 1) return bracelets[0];
      if (name.includes('bridge')) return bracelets.find((h) => h.includes('bridge')) || bracelets[0];
      if (name.includes('knot')) {
        return (
          bracelets.find((h) => h.includes('knot') && !h.includes('bridge')) ||
          bracelets.find((h) => h.includes('knot')) ||
          bracelets[1] ||
          bracelets[0]
        );
      }
      return bracelets[0];
    }

    if (looksLikeSizeValues(opt.values) || name.includes('ring')) {
      const rings = ringComponentHandles(components);
      if (rings.length === 1) return rings[0];
      const hints = [
        ['dainty', 'dainty'],
        ['initial', 'initial'],
        ['signet', 'signet'],
        ['bridge', 'bridge'],
        ['infinity', 'infinity'],
        ['pave', 'pave'],
      ];
      for (const [needle, token] of hints) {
        if (name.includes(needle)) {
          const hit = rings.find((h) => h.toLowerCase().includes(token));
          if (hit) return hit;
        }
      }
      if (name.includes('gold') && name.includes('silver')) {
        /* two-tone labels — fall through to index */
      } else if (name.includes('silver') && !name.includes('gold')) {
        const hit = rings.find((h) => h.includes('silver'));
        if (hit) return hit;
      } else if (name.includes('gold')) {
        const hit = rings.find((h) => h.includes('gold') && !h.includes('silver'));
        if (hit) return hit;
      }
      const ringOptsBefore = components.filter((_, idx) => idx < opt.index).length;
      return rings[Math.min(ringOptsBefore, rings.length - 1)] || rings[0];
    }

    const sized = components.filter((h) => memberNeedsSizePicker(h, bundleMembers?.[h]));
    if (sized.length === 1) return sized[0];
    return components[opt.index] || '';
  };

  const assignBundleOptionsToMembers = (product, selectable, bundleMembers) => {
    const components = getBundleComponentHandles(product.handle);
    const map = new Map();
    const usedOptions = new Set();

    selectable.forEach((opt) => {
      const member = resolveOptionMemberHandle(product.handle, opt, bundleMembers);
      if (
        member &&
        memberNeedsSizePicker(member, bundleMembers?.[member]) &&
        !map.has(member)
      ) {
        map.set(member, opt);
        usedOptions.add(opt.index);
      }
    });

    const remaining = selectable.filter((opt) => !usedOptions.has(opt.index));
    components
      .filter((h) => memberNeedsSizePicker(h, bundleMembers?.[h]) && !map.has(h))
      .forEach((handle, idx) => {
        if (remaining[idx]) map.set(handle, remaining[idx]);
      });

    return map;
  };

  const buildNativeBundleRows = (product, selectable, bundleMembers) => {
    const handles = getBundleComponentHandles(product.handle);
    const optionByMember = assignBundleOptionsToMembers(product, selectable, bundleMembers);
    return handles.map((handle) => ({
      handle,
      member: bundleMembers?.[handle],
      opt: optionByMember.get(handle) || null,
      displayOnly: !memberNeedsSizePicker(handle, bundleMembers?.[handle]),
    }));
  };

  const fetchMemberProducts = async (handles) => {
    const unique = [...new Set(handles.filter(Boolean))];
    const results = await Promise.allSettled(
      unique.map(async (handle) => {
        if (memberProductCache.has(handle)) return [handle, memberProductCache.get(handle)];
        const product = await fetchProduct(handle);
        memberProductCache.set(handle, product);
        return [handle, product];
      })
    );
    const entries = results
      .filter((r) => r.status === 'fulfilled')
      .map((r) => r.value);
    results
      .filter((r) => r.status === 'rejected')
      .forEach((r) => console.warn('[cart-quick-add] member product fetch failed', r.reason));
    return Object.fromEntries(entries);
  };

  const memberProductImage = (member) => {
    const raw = member?.featured_image || member?.images?.[0] || '';
    if (!raw) return '';
    return `${String(raw).split('?')[0]}?width=144`;
  };

  const renderBundleMemberLead = (member, memberHandle, optionLabel) => {
    if (!member) {
      return `<div class="cart-quick-add__bundle-lead cart-quick-add__bundle-lead--loading" aria-hidden="true"></div>`;
    }
    const url = `/products/${member.handle || memberHandle}`;
    const imgSrc = memberProductImage(member);
    return `
      <a class="cart-quick-add__bundle-lead" href="${escapeHtml(url)}" aria-label="${escapeHtml(member.title || optionLabel)}">
        <span class="cart-quick-add__bundle-media">
          ${
            imgSrc
              ? `<img src="${escapeHtml(imgSrc)}" alt="" width="56" height="56" loading="lazy" decoding="async">`
              : ''
          }
        </span>
      </a>`;
  };

  const renderBundleMemberName = (member, memberHandle, optionLabel) => {
    if (!member) return '';
    const url = `/products/${member.handle || memberHandle}`;
    const title = member.title || optionLabel;
    return `<a class="cart-quick-add__bundle-name" href="${escapeHtml(url)}" dir="auto">${escapeHtml(title)}</a>`;
  };

  const renderBundleInfoRow = (row) => {
    const { handle, member } = row;
    const optionLabel = member?.title || handle;
    return `
      <div class="cart-quick-add__option cart-quick-add__option--bundle-row cart-quick-add__option--bundle-info">
        <div class="cart-quick-add__bundle-row">
          ${renderBundleMemberLead(member, handle, optionLabel)}
          <div class="cart-quick-add__bundle-sizes">
            ${renderBundleMemberName(member, handle, optionLabel)}
          </div>
        </div>
      </div>`;
  };

  const renderBundleSizedRow = (product, row) => {
    const { handle, member, opt } = row;
    if (!opt) return renderBundleInfoRow(row);
    const isSize = true;
    const memberValues = member ? getMemberSizeValues(member) : [];
    const values = memberValues.length ? memberValues : opt.values;
    const compactRing =
      usesCompactRingPills(product, opt.name, values) || looksLikeSizeValues(values);
    const optionLabel = sizeOptionLabel(opt.name, values, product);
    const sortedValues = sortOptionValues(product, opt.name, values);
    const picked = state.memberSizes[handle] || state.selected[opt.index] || '';
    const pills = sortedValues
      .map((value, i) => {
        const id = `cart-qa-${handle}-${i}`;
        const checked = sizeValuesMatch(picked, value) || picked === value;
        const pillText =
          isSize || looksLikeSizeValues([value])
            ? pillDisplayFor(product, opt.name, values, value)
            : value;
        return `
          <label>
            <input type="radio" name="cart-quick-add-member-${handle}" id="${id}" value="${escapeHtml(value)}"
              data-cart-quick-add-option data-cart-quick-add-member data-member-handle="${escapeHtml(handle)}"
              data-option-index="${opt.index}"
              ${checked ? 'checked' : ''}>
            <span class="option-pill option-pill--pdp" dir="ltr" lang="en">${escapeHtml(pillText)}</span>
          </label>`;
      })
      .join('');
    const sizeDetail =
      isSize && compactRing
        ? `<p class="cart-quick-add__size-detail" data-cart-quick-add-size-detail data-option-index="${opt.index}" aria-live="polite" dir="auto"></p>`
        : '';
    const optionClasses = [
      'cart-quick-add__option',
      isSize && compactRing ? 'cart-quick-add__option--ring-size' : '',
      isSize && !compactRing ? 'cart-quick-add__option--apparel-size' : '',
      'cart-quick-add__option--bundle-row',
    ]
      .filter(Boolean)
      .join(' ');
    const memberTitle = member?.title || optionLabel;

    return `
      <div class="${optionClasses}">
        <div class="cart-quick-add__bundle-row">
          ${renderBundleMemberLead(member, handle, memberTitle)}
          <div class="cart-quick-add__bundle-sizes">
            ${renderBundleMemberName(member, handle, memberTitle)}
            <div class="cart-quick-add__option-head">
              <span class="cart-quick-add__option-label t-cap-ltr" dir="auto">${escapeHtml(optionLabel)}:</span>
              ${sizeDetail}
            </div>
            <div class="option-pills option-pills--pdp cart-quick-add__pills" role="radiogroup" aria-label="${escapeHtml(optionLabel)}">
              ${pills}
            </div>
          </div>
        </div>
      </div>`;
  };

  const getEls = () => {
    const root = deps.cartDrawer || document.querySelector('[data-cart-drawer]');
    if (!root) return null;
    return {
      root,
      panel: root.querySelector('[data-cart-quick-add]'),
      product: root.querySelector('[data-cart-quick-add-product]'),
      options: root.querySelector('[data-cart-quick-add-options]'),
      status: root.querySelector('[data-cart-quick-add-status]'),
      submit: root.querySelector('[data-cart-quick-add-submit]'),
      submitLabel: root.querySelector('[data-cart-quick-add-submit-label]'),
      wishlist: root.querySelector('[data-cart-quick-add-wishlist]'),
    };
  };

  const updateWishlistBtn = (els, product, variant) => {
    if (!els?.wishlist || !product) return;
    els.wishlist.hidden = false;
    els.wishlist.dataset.wishlistToggle = String(product.id);
    els.wishlist.dataset.productHandle = product.handle;
    els.wishlist.dataset.variantId = variant ? String(variant.id) : '';
    els.wishlist.dataset.productUrl = `/products/${product.handle}`;
  };

  const updateUI = () => {
    const els = getEls();
    const s = strings();
    if (!els?.panel || !state.product) return;

    const selectable = getSelectableOptions(state.product);
    const showMemberRows =
      isNativeBundleProduct(state.product) &&
      getBundleComponentHandles(state.product.handle).length > 0;

    if (showMemberRows) {
      if (!Object.keys(state.memberSizes).length) {
        state.memberSizes = initMemberSizes(state.product, state.bundleMembers);
      }
      syncSelectedFromMemberSizes(state.product, state.bundleMembers);
      state.variant = findMultiBundleVariant(
        state.product,
        state.memberSizes,
        state.bundleMembers
      );
    } else {
      state.variant = findVariant(state.product, state.selected);
    }

    const bundleRows =
      showMemberRows && Object.keys(state.bundleMembers).length
        ? buildNativeBundleRows(state.product, selectable, state.bundleMembers)
        : [];
    const missingSize = showMemberRows
      ? bundleRows.some(
          (row) =>
            !row.displayOnly &&
            row.opt &&
            (state.memberSizes[row.handle] == null || state.memberSizes[row.handle] === '')
        )
      : selectable.some(
          (opt) =>
            isSizeOption(opt.name, opt.values) &&
            (state.selected[opt.index] == null || state.selected[opt.index] === '')
        );

    if (els.product) {
      const image = state.variant?.featured_image?.src || state.product.featured_image || state.product.images?.[0];
      const imgSrc = image ? String(image).split('?')[0] + '?width=240' : '';
      const material = productMaterialLine(state.product);
      const price = state.variant?.price ?? state.product.variants?.[0]?.price ?? 0;
      const compare = state.variant?.compare_at_price;
      els.product.hidden = false;
      els.product.innerHTML = `
        <div class="cart-quick-add__product-media">
          ${imgSrc ? `<img src="${escapeHtml(imgSrc)}" alt="" width="88" height="88" loading="lazy" decoding="async">` : ''}
        </div>
        <div class="cart-quick-add__product-meta">
          <p class="cart-quick-add__product-title" dir="auto">${escapeHtml(state.product.title)}</p>
          ${material ? `<p class="cart-quick-add__product-material" dir="auto">${escapeHtml(material)}</p>` : ''}
          <p class="cart-quick-add__product-price" data-cart-quick-add-price>
            ${compare && compare > price ? `<del>${escapeHtml(money(compare))}</del>` : ''}
            <span>${escapeHtml(money(price))}</span>
          </p>
        </div>`;
    }

    if (els.options) {
      const bundleMode = isNativeBundleProduct(state.product) || selectable.length > 1;
      if (bundleMode) {
        els.panel.classList.add('cart-quick-add--bundle');
      } else {
        els.panel.classList.remove('cart-quick-add--bundle');
      }

      let optionBlocks = '';
      if (showMemberRows) {
        optionBlocks = bundleRows
          .map((row) =>
            row.displayOnly
              ? renderBundleInfoRow(row)
              : renderBundleSizedRow(state.product, row)
          )
          .join('');
      } else {
        optionBlocks = selectable
          .map((opt) => {
            const isSize = isSizeOption(opt.name, opt.values);
            const compactRing = usesCompactRingPills(state.product, opt.name, opt.values);
            const optionLabel = sizeOptionLabel(opt.name, opt.values, state.product);
            const sortedValues = sortOptionValues(state.product, opt.name, opt.values);
            const pills = sortedValues
              .map((value, i) => {
                const id = `cart-qa-${opt.index}-${i}`;
                const checked = state.selected[opt.index] === value;
                const available = isValueAvailable(state.product, opt.index, value, state.selected);
                const pillText =
                  isSize || looksLikeSizeValues([value])
                    ? pillDisplayFor(state.product, opt.name, opt.values, value)
                    : value;
                return `
                  <label>
                    <input type="radio" name="cart-quick-add-opt-${opt.index}" id="${id}" value="${escapeHtml(value)}"
                      data-cart-quick-add-option data-option-index="${opt.index}"
                      ${checked ? 'checked' : ''} ${available ? '' : 'disabled'}>
                    <span class="option-pill option-pill--pdp" dir="ltr" lang="en">${escapeHtml(pillText)}</span>
                  </label>`;
              })
              .join('');
            const sizeDetail =
              isSize && compactRing
                ? `<p class="cart-quick-add__size-detail" data-cart-quick-add-size-detail data-option-index="${opt.index}" aria-live="polite" dir="auto"></p>`
                : '';
            const sizeGuide =
              isSize && !bundleMode
                ? `<button class="cart-quick-add__size-guide" type="button" data-drawer-open="[data-size-drawer]" data-size-segment="${optionSizeSegment(state.product, opt.name, opt.values)}" dir="auto">${escapeHtml(s.sizeGuide || 'Size guide')}</button>`
                : '';
            const optionClasses = [
              'cart-quick-add__option',
              isSize && compactRing ? 'cart-quick-add__option--ring-size' : '',
              isSize && !compactRing ? 'cart-quick-add__option--apparel-size' : '',
            ]
              .filter(Boolean)
              .join(' ');

            return `
              <div class="${optionClasses}">
                <div class="cart-quick-add__option-head">
                  <span class="cart-quick-add__option-label t-cap-ltr" dir="auto">${escapeHtml(optionLabel)}:</span>
                  ${sizeDetail}
                </div>
                <div class="option-pills option-pills--pdp cart-quick-add__pills" role="radiogroup" aria-label="${escapeHtml(optionLabel)}">
                  ${pills}
                </div>
                ${sizeGuide}
              </div>`;
          })
          .join('');
      }

      const sharedSizeGuide =
        bundleMode && selectable.some((opt) => isSizeOption(opt.name, opt.values))
          ? `<div class="cart-quick-add__options-footer">
              <button class="cart-quick-add__size-guide" type="button" data-drawer-open="[data-size-drawer]" data-size-segment="${sizeGuideSegmentFor(state.product, selectable)}" dir="auto">${escapeHtml(s.sizeGuide || 'Size guide')}</button>
            </div>`
          : '';

      els.options.innerHTML = optionBlocks + sharedSizeGuide;
    }

    if (els.status) {
      if (missingSize) {
        els.status.textContent = s.selectSize || 'Select size for availability';
        els.status.dataset.state = 'hint';
      } else if (!state.variant) {
        els.status.textContent = s.unavailable || 'Unavailable';
        els.status.dataset.state = 'error';
      } else if (!state.variant.available) {
        els.status.textContent = s.soldOut || 'Sold out';
        els.status.dataset.state = 'error';
      } else {
        els.status.textContent = '';
        els.status.dataset.state = '';
      }
    }

    if (els.submit) {
      const canAdd = state.variant?.available && !missingSize;
      els.submit.disabled = !canAdd;
      if (els.submitLabel) {
        els.submitLabel.textContent = canAdd
          ? s.addToBag || 'Add to Bag'
          : missingSize
            ? s.addToBag || 'Add to Bag'
            : s.soldOut || 'Sold out';
      }
    }

    updateWishlistBtn(els, state.product, state.variant);
    updateSizeDetail(els);
  };

  const updateSizeDetail = (els) => {
    if (!els?.root || !state.product) return;
    const details = els.root.querySelectorAll('[data-cart-quick-add-size-detail]');
    if (!details.length) return;
    const s = strings();
    let template = s.sizeDetailUs || 'US {{ us_size }} · {{ size_label }}';
    if (/translation missing/i.test(template)) template = 'US {{ us_size }} · {{ size_label }}';

    details.forEach((detail) => {
      const optionIndex = detail.dataset.optionIndex;
      const checked =
        optionIndex != null
          ? els.root.querySelector(
              `[data-cart-quick-add-option][data-option-index="${optionIndex}"]:checked`
            )
          : els.root.querySelector('[data-cart-quick-add-option]:checked');
      if (!checked) {
        detail.textContent = '';
        return;
      }
      const parsed = parseRingSizeValue(checked.value);
      const us = parsed.us || parsed.pill || '';
      const sizeLabel = parsed.label || '';
      if (!us || !/^\d/.test(String(us).trim())) {
        detail.textContent = '';
        return;
      }
      detail.textContent = formatUsSizeDetail(template, us, sizeLabel);
    });
  };

  const pickDefaultSelections = (product) => {
    const selected = Array.from(
      { length: Math.max((product.options || []).length, 3) },
      () => undefined
    );
    const componentHandles = isNativeBundleProduct(product)
      ? getBundleComponentHandles(product.handle)
      : [];
    getSelectableOptions(product).forEach((opt) => {
      const sorted = sortOptionValues(product, opt.name, opt.values);
      if (componentHandles.length) {
        if (sorted[0]) selected[opt.index] = sorted[0];
        return;
      }
      const firstAvailable = sorted.find((value) =>
        isValueAvailable(product, opt.index, value, selected)
      );
      if (firstAvailable) selected[opt.index] = firstAvailable;
    });
    return selected;
  };

  const openPanel = async (product, options = {}) => {
    const els = getEls();
    if (!els?.panel || !product) return;
    const componentHandles = isNativeBundleProduct(product)
      ? getBundleComponentHandles(product.handle)
      : [];
    state = {
      product,
      selected: pickDefaultSelections(product),
      variant: null,
      trigger: options.trigger || null,
      openCart: options.openCart !== false,
      bundleMembers: {},
      bundleMembersLoading: componentHandles.length > 0,
      memberSizes: {},
    };
    if (componentHandles.length) {
      state.memberSizes = initMemberSizes(product, {});
    }
    els.root.classList.add('is-quick-add-open');
    els.panel.hidden = false;
    els.panel.dataset.open = 'true';
    els.panel.setAttribute('aria-hidden', 'false');
    updateUI();

    if (componentHandles.length) {
      try {
        state.bundleMembers = await fetchMemberProducts(componentHandles);
        state.memberSizes = initMemberSizes(state.product, state.bundleMembers);
      } catch (err) {
        console.error(err);
      } finally {
        state.bundleMembersLoading = false;
        if (state.product?.handle === product.handle) updateUI();
      }
    }
  };

  const closePanel = () => {
    const els = getEls();
    const root = els?.root || document.querySelector('[data-cart-drawer]');
    root?.classList.remove('is-quick-add-open');
    if (!els?.panel) return;
    els.panel.hidden = true;
    els.panel.dataset.open = 'false';
    els.panel.setAttribute('aria-hidden', 'true');
    state = {
      product: null,
      selected: [],
      variant: null,
      trigger: null,
      openCart: true,
      bundleMembers: {},
      bundleMembersLoading: false,
      memberSizes: {},
    };
  };

  const fetchProduct = async (handle) => {
    const root = window.Shopify?.routes?.root || '/';
    const url = `${root}products/${encodeURIComponent(handle)}.js`;
    const opts = { headers: { Accept: 'application/json' }, credentials: 'same-origin' };
    const nativeFetch = window.__GhaliaNet?.fetch || window.fetch.bind(window);
    let res;
    try {
      res = await nativeFetch(url, opts);
    } catch (_) {
      res = null;
    }
    if (!res?.ok) {
      await new Promise((r) => setTimeout(r, 160));
      try {
        res = await nativeFetch(url, opts);
      } catch (_) {
        res = null;
      }
    }
    if (!res?.ok) throw new Error('Product not found');
    return res.json();
  };

  const tryDirectVariantAdd = async (variantId, btn, openCart) => {
    if (!variantId || !deps.addVariantToCart) return false;
    await deps.addVariantToCart(variantId, btn, { openCart, skipStockCheck: true });
    return true;
  };

  const productPageUrl = (btn) => {
    const card = btn.closest('[data-product-card]');
    return (
      btn.getAttribute('data-product-url') ||
      card?.querySelector('[data-product-url]')?.getAttribute('href') ||
      card?.querySelector('.product-card__title')?.getAttribute('href') ||
      (card?.dataset.productHandle ? `${window.Shopify?.routes?.root || '/'}products/${card.dataset.productHandle}` : '')
    );
  };

  const resolveHandle = (btn) => {
    const direct = btn.getAttribute('data-quick-add-handle');
    if (direct) return direct.trim();
    const card = btn.closest('[data-product-card]');
    if (card?.dataset.productHandle) return card.dataset.productHandle.trim();
    const wishHandle = btn.dataset.productHandle;
    if (wishHandle) return wishHandle.trim();
    return '';
  };

  const handleQuickAddClick = async (btn) => {
    ensureDeps();
    const variantId = btn.getAttribute('data-quick-add');
    const handle = resolveHandle(btn);
    const openCart = btn.getAttribute('data-open-cart') !== 'false';

    if (handle) {
      let product = null;
      try {
        product = await fetchProduct(handle);
      } catch (fetchErr) {
        const pdpUrl = productPageUrl(btn);
        console.error(fetchErr);
        document.dispatchEvent(
          new CustomEvent('ghalia:cart:error', {
            detail: new Error(
              window.Shopify?.strings?.quickAdd?.selectSize ||
                'Please open the product page to choose your size.'
            ),
          })
        );
        if (pdpUrl) window.location.assign(pdpUrl);
        return true;
      }
      try {
        if (needsQuickAdd(product)) {
          await openQuickAddPicker(product, btn, openCart);
          return true;
        }
        const fallbackId =
          variantId && product.variants?.some((v) => String(v.id) === String(variantId) && v.available)
            ? variantId
            : String(product.variants?.find((v) => v.available)?.id || '');
        if (fallbackId && deps.addVariantToCart) {
          await deps.addVariantToCart(fallbackId, btn, { openCart, skipStockCheck: true });
          return true;
        }
        const soldOut = window.Shopify?.strings?.quickAdd?.soldOut || 'Sold out';
        document.dispatchEvent(new CustomEvent('ghalia:cart:error', { detail: new Error(soldOut) }));
        return true;
      } catch (err) {
        console.error(err);
        if (!err._ghaliaCartToast) {
          document.dispatchEvent(new CustomEvent('ghalia:cart:error', { detail: err }));
        }
        return true;
      }
    }

    if (variantId && deps.addVariantToCart) {
      await deps.addVariantToCart(variantId, btn, { openCart, skipStockCheck: true });
      return true;
    }
    return false;
  };

  const bind = () => {
    if (window.__ghCartQuickAddBound) return;
    window.__ghCartQuickAddBound = true;
    document.addEventListener('click', async (e) => {
      const btn = e.target.closest('[data-quick-add], [data-quick-add-handle]');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      if (btn.getAttribute('aria-busy') === 'true') return;
      await handleQuickAddClick(btn);
    });

    document.addEventListener('change', (e) => {
      const input = e.target.closest('[data-cart-quick-add-option]');
      if (!input || !state.product) return;
      const index = parseInt(input.dataset.optionIndex, 10);
      const memberHandle = input.dataset.memberHandle;
      if (!Number.isNaN(index)) state.selected[index] = input.value;
      if (memberHandle) state.memberSizes[memberHandle] = input.value;
      updateUI();
    });

    document.addEventListener('click', async (e) => {
      const closeBtn = e.target.closest('[data-cart-quick-add-close]');
      if (closeBtn) {
        e.preventDefault();
        e.stopPropagation();
        closePanel();
        return;
      }
      const submit = e.target.closest('[data-cart-quick-add-submit]');
      if (!submit || !deps.addVariantToCart) return;
      e.preventDefault();
      if (!state.product) return;
      if (!state.variant?.available) {
        updateUI();
        if (!state.variant?.available) return;
      }
      submit.setAttribute('aria-busy', 'true');
      try {
        await deps.addVariantToCart(String(state.variant.id), submit, {
          openCart: state.openCart,
          skipStockCheck: true,
        });
        closePanel();
      } catch (err) {
        console.error(err);
      } finally {
        submit.removeAttribute('aria-busy');
      }
    });

    document.addEventListener('change', (e) => {
      const sizeInput = e.target.closest('[data-pdp-set-size]');
      if (!sizeInput) return;
      const root = sizeInput.closest('[data-pdp-set]');
      const addBtn = root?.querySelector('[data-pdp-set-add]');
      const variantId = sizeInput.dataset.setVariantId;
      if (addBtn && variantId) addBtn.setAttribute('data-quick-add', variantId);
    });

    document.addEventListener('click', async (e) => {
      const btn = e.target.closest('[data-pdp-bundle-add]');
      if (!btn || btn.getAttribute('aria-busy') === 'true') return;
      e.preventDefault();
      const members = String(btn.dataset.stackMembers || '')
        .split(',')
        .map((h) => h.trim());
      const anchor = String(btn.dataset.stackAnchor || '').trim();
      const nativeAnchors = window.GhaliaNativeBundleAnchors;
      if (nativeAnchors?.has(anchor) || members.some((h) => nativeAnchors?.has(h))) {
        window.alert('Use Add complete set above (with sizes) for this bundle.');
        return;
      }
      if (
        members.includes('infinity-bridge-ring') &&
        members.includes('single-infinity-pave-ring')
      ) {
        window.alert(
          'Use Add complete set above (with sizes) for the Infinity Ring Duo bundle.'
        );
        return;
      }
      if (
        members.includes('knot-bridge-ring-silver') &&
        members.includes('knot-bridge-bracelet-silver')
      ) {
        window.alert(
          'Use Add complete set above (with sizes) for the KNOT Bridge Ring + Bracelet duo.'
        );
        return;
      }
      const ids = String(btn.dataset.variantIds || '')
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean);
      if (!ids.length) return;
      btn.setAttribute('aria-busy', 'true');
      const stackProps = btn.dataset.stackAnchor
        ? {
            _ghalia_stack_anchor: btn.dataset.stackAnchor,
            _ghalia_stack_pct: String(btn.dataset.discountPct || ''),
            _ghalia_stack_members: btn.dataset.stackMembers || '',
            _ghalia_bundle_line: 'true',
          }
        : null;
      try {
        if (window.GhaliaCart?.addItems) {
          await window.GhaliaCart.addItems(
            ids.map((id) => ({
              id: Number(id),
              quantity: 1,
              ...(stackProps ? { properties: { ...stackProps } } : {}),
            })),
            btn,
            { openCart: true }
          );
        } else if (window.GhaliaCartApi?.addItem) {
          await window.GhaliaCartApi.addItem({
            items: ids.map((id) => ({
              id: Number(id),
              quantity: 1,
              ...(stackProps ? { properties: { ...stackProps } } : {}),
            })),
          });
          if (deps.openDrawer && deps.cartDrawer) deps.openDrawer(deps.cartDrawer);
        } else {
          throw new Error('Could not add stack');
        }
      } catch (err) {
        console.error(err);
      } finally {
        btn.removeAttribute('aria-busy');
      }
    });
  };

  window.GhaliaCartQuickAdd = {
    init(api) {
      deps = api || {};
      bind();
    },
    close: closePanel,
    needsQuickAdd,
    open: openPanel,
  };

  if (window.GhaliaCartQuickAddDeps) {
    window.GhaliaCartQuickAdd.init(window.GhaliaCartQuickAddDeps);
  }
})();
